import { Component } from 'react';
import Head from 'next/head';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { ReactSVG } from 'react-svg';
import key from 'weak-key';
import SiteSettings from '~/siteSettings';
import { getTaxonomyTerms, stripTags, getIcon, isGiveAwayLink } from '~/utils/utils';
import PageWrapper from '~/components/wrappers/pageWrapper';

const Utag = dynamic(() => import('~/components/utag/utag'), { ssr: false });

/**
 * Taxonomy
 * @category Page
 * @extends Component
 * @summary Display the taxonomy page.
 * @return {Node} React node containing the taxonomy page.
 */

class Taxonomy extends Component {
  constructor(props) {
    super(props);
    this.state = {
      itemsPerPage: 16,
      offset: 0,
      data: props.data,
      loading: false
    };
  }

  getMore = async () => {
    const { itemsPerPage, offset, data } = this.state;
    const incrementOffset = offset + itemsPerPage;

    this.setState({
      loading: true
    });

    const updatedData = await getTaxonomyTerms({
      machine_name: 'latest_content_by_term',
      display: 'master',
      itemsPerPage,
      offset: incrementOffset,
      viewArgs: [
        'video,article,gallery,recipe',
        data.taxonomyTermQuery.ctid.toString()
      ],
      target_id: data.taxonomyTermQuery.ctid
    });

    this.setState({
      loading: false,
      offset: incrementOffset,
      data: {
        ...data,
        viewQuery: {
          count: data.viewQuery.count,
          resultRow: data.viewQuery.resultRow.concat(updatedData.viewQuery.resultRow)
        }
      }
    });
  }

  render() {
    const { router, baseUrl, sitePrimaryRsid } = this.props;
    const { data, loading } = this.state;
    const { taxonomyTermQuery, viewQuery } = data;
    const path = router.asPath;

    return (
      <>
        <Head>
          <title>{ taxonomyTermQuery.name } | { SiteSettings.siteName }</title>
          <meta name='title' content={ taxonomyTermQuery.name } />
          <link rel='canonical' href={`${ baseUrl }${ path }`} />
          <meta name="keywords" content={ SiteSettings.defaultKeywords } />
          <meta name="description" content={ taxonomyTermQuery.field_seo_description } />
        </Head>
        <div className='constrain'>
          <div className='taxonomy__results'>
            { viewQuery.count } Results
          </div>
          <h1 className='taxonomy__header'>
            { taxonomyTermQuery.name }
          </h1>
          {viewQuery.resultRow.map((item) => (
            <Link legacyBehavior key={`tax-${key(item)}`} href={ item.path }>
              <a aria-label={`Visit ${ item.title }`} target={ isGiveAwayLink(item?.path) ? '_blank' : '_self'}>
                <div className='taxonomy__item'>
                  <div className='taxonomy__item-img'>
                    <div className='wrap-relative'>
                      <div className='taxonomy__item-icon'>
                        <ReactSVG src={ getIcon(item.__typename) } />
                      </div>
                      { item?.field_image?.field_image?.lg?.url &&
                        <picture>
                          <source media={ `(max-width: ${ SiteSettings.breakpoints.sm })` } srcSet={ item?.field_image?.field_image?.sm?.url } />
                          <img
                            src={ item.field_image.field_image.lg.url }
                            alt={ item.field_image?.alt }
                            className='img-full'
                            loading='lazy'
                          />
                        </picture>
                      }
                    </div>
                  </div>
                  <div className='taxonomy__item-desc'>
                    <div>
                      <h3>{ item.title }</h3>
                      <h4>{ item.field_subhead }</h4>
                      { item.body &&
                        <div className='taxonomy__item-body' dangerouslySetInnerHTML={{__html: stripTags(item.body).substring(0, 100)}}></div>
                      }
                      {/* @TODO: DREW-287 - We cannot yet select category / path at this level */}
                      { item.field_category && item.field_category.name &&
                        <div className='taxonomy__item-category'>
                          { item.field_category.name }
                        </div>
                      }
                    </div>
                  </div>
                </div>
              </a>
            </Link>
          ))}
        </div>
        <div className='text-center'>
          { viewQuery.count > viewQuery.resultRow.length &&
            <button
              type='button'
              className='btn__cta'
              aria-label='Load More'
              onClick={ this.getMore }
            >
              <span>{ loading && <img src='/img/loader.gif' alt='Loading' /> } see more</span>
            </button>
          }
        </div>
        <div className='spacer spacer--2'></div>
        <Utag
          pageType='section_door'
          siteSection={ taxonomyTermQuery.name }
          data={{ sitePrimaryRsid }}
        />
      </>
    );
  }
}

export async function getServerSideProps(ctx) {
  const data = await getTaxonomyTerms({
    machine_name: 'latest_content_by_term',
    display: 'master',
    itemsPerPage: 16,
    offset: 0,
    viewArgs: [
      'video,article,gallery,recipe',
      ctx.query.nid
    ],
    target_id: parseInt(ctx.query.nid, 10)
  });

  return {
    props: {
      data
    }
  }
}

export default PageWrapper(Taxonomy);
